import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { PaginationInfo } from '@/types';
import { cn } from '@/lib/utils';

interface PaginationProps {
  pagination: PaginationInfo;
  onPageChange: (page: number) => void;
  itemLabel?: string;
  className?: string;
}

export function Pagination({
  pagination,
  onPageChange,
  itemLabel = 'items',
  className,
}: PaginationProps) {
  const { page, pageSize, total } = pagination;
  const totalPages = Math.max(1, pagination.totalPages);
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  const pages = getVisiblePages(page, totalPages);

  if (totalPages <= 1 && total <= pageSize) return null;

  return (
    <nav
      aria-label={`${itemLabel} pagination`}
      className={cn(
        'flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between',
        className
      )}
    >
      <p className="text-sm text-muted-foreground" aria-live="polite">
        Showing {start}–{end} of {total} {itemLabel}
      </p>
      <div className="flex items-center justify-between gap-2 sm:justify-end">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          aria-label="Previous page"
        >
          <ChevronLeft className="mr-1 h-4 w-4" />
          Previous
        </Button>
        <div className="hidden items-center gap-1 sm:flex">
          {pages.map((entry, index) =>
            entry === 'ellipsis' ? (
              <span key={`ellipsis-${index}`} className="px-2 text-sm text-muted-foreground">
                …
              </span>
            ) : (
              <Button
                key={entry}
                type="button"
                variant={entry === page ? 'default' : 'ghost'}
                size="icon"
                className="h-10 w-10"
                onClick={() => onPageChange(entry)}
                aria-label={`Page ${entry}`}
                aria-current={entry === page ? 'page' : undefined}
              >
                {entry}
              </Button>
            )
          )}
        </div>
        <span className="text-sm text-muted-foreground sm:hidden">
          {page} / {totalPages}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          aria-label="Next page"
        >
          Next
          <ChevronRight className="ml-1 h-4 w-4" />
        </Button>
      </div>
    </nav>
  );
}

function getVisiblePages(page: number, totalPages: number): Array<number | 'ellipsis'> {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const entries: Array<number | 'ellipsis'> = [1];
  if (page > 3) entries.push('ellipsis');

  for (let current = Math.max(2, page - 1); current <= Math.min(totalPages - 1, page + 1); current += 1) {
    entries.push(current);
  }

  if (page < totalPages - 2) entries.push('ellipsis');
  entries.push(totalPages);
  return entries;
}
